import axios, { type InternalAxiosRequestConfig } from 'axios'

import { config } from '../config'
import { attachMicroserviceAuthRetry } from '../sso/interceptors/microservice-auth-retry.interceptor'
import { resolvePaymentsAccessToken } from '../sso/services/payments-access.service'
import { isSuiteSsoRedirectMode } from '../sso/sso-flags'
import { logWarn } from '../sso/utils/sso-logger'

function getStoredPayRefreshToken(): string {
  const raw = globalThis.sessionStorage.getItem('token') || globalThis.localStorage.getItem('token')
  return raw?.replaceAll('"', '') ?? ''
}

/**
 * Payments API client with automatic auth.
 * SSO mode → Pay RS256 access token bridged from Identity.
 */
export const PaymentsHttp = axios.create({
  timeout: 18000000,
  baseURL: config.api.payments.baseUrl,
  withCredentials: false
})

PaymentsHttp.interceptors.request.use(async (requestConfig: InternalAxiosRequestConfig) => {
  if (isSuiteSsoRedirectMode()) {
    const payAccessToken = await resolvePaymentsAccessToken(getStoredPayRefreshToken())
    requestConfig.headers.Authorization = `Bearer ${payAccessToken}`
    return requestConfig
  }

  const hasExplicitAuth =
    typeof requestConfig.headers?.Authorization === 'string' &&
    requestConfig.headers.Authorization.length > 0

  if (hasExplicitAuth) {
    return requestConfig
  }

  try {
    const accessToken = await resolvePaymentsAccessToken(getStoredPayRefreshToken())
    requestConfig.headers.Authorization = `Bearer ${accessToken}`
  } catch (error) {
    logWarn('PaymentsHttp — could not resolve payments access token', { error })
    throw error
  }

  return requestConfig
})

attachMicroserviceAuthRetry(PaymentsHttp)
